// frontend/src/pages/AreaFormPage.tsx
// NEW: Mobile-first page for adding/editing areas (replaces modal)

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Save, 
  Loader2, 
  AlertCircle,
  Eye
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import FormField from '@/components/ui/FormField';

// Import from new refactored API structure
import { 
  useCreateArea, 
  useUpdateArea, 
  useArea,
  useValidatePincode,
  usePincodeSuggestions
} from '@/hooks/useAreas';
import { 
  CreateAreaData,
  UpdateAreaData
} from '@/lib/api/areasService';

const AreaFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useAuth();
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isEditMode = !!id;
  const isViewMode = location.pathname.includes('/view');
  const readOnly = isAdmin || isViewMode;
  
  // Form state
  const [formData, setFormData] = useState({
    pincode: '',
    city: '',
    state: '',
    district: '',
    region: '',
    is_active: true
  });
  
  const [errors, setErrors] = useState<Record<string, string | string[]>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showPincodeSuggestions, setShowPincodeSuggestions] = useState(false);
  const [pincodeSuggestions, setPincodeSuggestions] = useState<Array<{
    pincode: string;
    city: string;
    state: string;
    district: string;
    region: string;
  }>>([]);

  // API hooks
  const { data: existingArea, isLoading: isLoadingArea } = useArea(id ? parseInt(id) : 0);
  const createAreaMutation = useCreateArea();
  const updateAreaMutation = useUpdateArea();
  const validatePincodeMutation = useValidatePincode();
  const pincodeSuggestionsMutation = usePincodeSuggestions();

  // Initialize form data
  useEffect(() => {
    if (existingArea) {
      setFormData({
        pincode: existingArea.pincode || '',
        city: existingArea.city || '',
        state: existingArea.state || '',
        district: existingArea.district || '',
        region: existingArea.region || '',
        is_active: existingArea.is_active ?? true
      });
    }
  }, [existingArea]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Handle pincode validation
    if (name === 'pincode' && typeof newValue === 'string' && newValue.length === 6) {
      validatePincode(newValue);
    }
  };

  // Handle blur events
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  // Validate pincode
  const validatePincode = async (pincode: string) => {
    try {
      const result = await validatePincodeMutation.mutateAsync(pincode);
      if (result && result.valid) {
        setFormData(prev => ({
          ...prev,
          city: result.city || prev.city,
          state: result.state || prev.state,
          district: result.district || prev.district,
          region: result.region || prev.region
        }));
        
        // Mark related fields as touched since they're being auto-filled
        setTouched(prev => ({
          ...prev,
          city: true,
          state: true,
          district: true,
          region: true
        }));
      }
    } catch (error) {
      console.error('Pincode validation failed:', error);
    }
  };

  // Get pincode suggestions
  const getPincodeSuggestions = async (query: string) => {
    if (query.length >= 3) {
      try {
        const result = await pincodeSuggestionsMutation.mutateAsync(query);
        if (result && result.suggestions) {
          setPincodeSuggestions(result.suggestions);
          setShowPincodeSuggestions(true);
        }
      } catch (error) {
        console.error('Failed to get pincode suggestions:', error);
      }
    } else {
      setShowPincodeSuggestions(false);
    }
  };

  // Select pincode suggestion
  const selectPincodeSuggestion = (suggestion: typeof pincodeSuggestions[0]) => {
    setFormData(prev => ({
      ...prev,
      pincode: suggestion.pincode,
      city: suggestion.city,
      state: suggestion.state,
      district: suggestion.district,
      region: suggestion.region
    }));
    
    // Mark all fields as touched since they're being auto-filled
    setTouched(prev => ({
      ...prev,
      pincode: true,
      city: true,
      state: true,
      district: true,
      region: true
    }));
    
    setShowPincodeSuggestions(false);
  };

  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (readOnly) return;
    
    if (!validateForm()) return;

    try {
      if (isEditMode && existingArea) {
        await updateAreaMutation.mutateAsync({
          id: existingArea.area_id,
          data: formData as UpdateAreaData
        });
      } else {
        await createAreaMutation.mutateAsync(formData as CreateAreaData);
      }
      
      // Navigate back with success message
      navigate('/provider-areas', {
        state: { 
          message: `Service area ${isEditMode ? 'updated' : 'created'} successfully!`,
          type: 'success'
        }
      });
    } catch (error: any) {
      console.error('Form submission failed:', error);
      
      // Handle validation errors from backend
      if (error.response?.data?.errors || error.errors || error.data?.errors) {
        const apiErrors = error.response?.data?.errors || error.errors || error.data?.errors;
        const newErrors: Record<string, string | string[]> = {};
        
        Object.entries(apiErrors).forEach(([field, messages]) => {
          newErrors[field] = Array.isArray(messages) ? messages : [messages as string];
        });
        
        setErrors(newErrors);
        
        // Mark fields with errors as touched
        const newTouched: Record<string, boolean> = {};
        Object.keys(newErrors).forEach(field => {
          newTouched[field] = true;
        });
        setTouched(prev => ({ ...prev, ...newTouched }));
      } else {
        // General error - show as pincode error for now
        const errorMessage = error.response?.data?.message || 
                           error.message || 
                           error.data?.message ||
                           'An unexpected error occurred. Please try again.';
        setErrors({ general: errorMessage });
      }
    }
  };

  // Loading state
  if (isLoadingArea) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading area details...</p>
        </div>
      </div>
    );
  }

  const isLoading = createAreaMutation.isPending || updateAreaMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate('/provider-areas')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back</span>
          </button>
          
          <h1 className="text-lg font-semibold text-gray-900">
            {readOnly ? 'View Area' : isEditMode ? 'Edit Area' : 'Add Area'}
          </h1>
          
          <div className="w-16"> {/* Spacer for center alignment */}
            {!readOnly && (
              <button
                type="submit"
                form="area-form"
                disabled={isLoading}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="px-4 py-6">
        <form id="area-form" onSubmit={handleSubmit} className="space-y-6">
          
          {/* Admin Notice */}
          {isAdmin && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900">Admin View</span>
              </div>
              <p className="text-sm text-blue-700">
                You're viewing this area in read-only mode. Only providers can modify their service areas.
              </p>
            </div>
          )}

          {/* Pincode Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-green-600" />
              Location Details
            </h3>
            
            <div className="space-y-4">
              {/* General error display */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-red-800">
                        {Array.isArray(errors.general) ? errors.general.join(', ') : errors.general}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pincode with suggestions */}
              <div className="relative">
                <FormField
                  label="Pincode"
                  name="pincode"
                  type="text"
                  value={formData.pincode}
                  onChange={(e) => {
                    handleInputChange(e);
                    getPincodeSuggestions(e.target.value);
                  }}
                  onBlur={handleBlur}
                  placeholder="Enter 6-digit pincode"
                  required
                  disabled={readOnly}
                  error={errors.pincode}
                  touched={touched.pincode}
                  className="relative"
                  inputClassName={readOnly ? 'bg-gray-50' : ''}
                  helperText="Enter a valid 6-digit Indian postal code"
                />
                
                {validatePincodeMutation.isPending && (
                  <div className="absolute right-3 top-9 transform -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  </div>
                )}
                
                {/* Pincode suggestions */}
                {showPincodeSuggestions && pincodeSuggestions.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {pincodeSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => selectPincodeSuggestion(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{suggestion.pincode}</div>
                        <div className="text-sm text-gray-600">
                          {suggestion.city}, {suggestion.state}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* City */}
              <FormField
                label="City"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="Enter city name"
                required
                disabled={readOnly}
                error={errors.city}
                touched={touched.city}
                inputClassName={readOnly ? 'bg-gray-50' : ''}
                helperText="City name will be auto-filled from pincode"
              />

              {/* State */}
              <FormField
                label="State"
                name="state"
                type="text"
                value={formData.state}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="Enter state name"
                required
                disabled={readOnly}
                error={errors.state}
                touched={touched.state}
                inputClassName={readOnly ? 'bg-gray-50' : ''}
                helperText="State name will be auto-filled from pincode"
              />

              {/* District (Optional) */}
              <FormField
                label="District"
                name="district"
                type="text"
                value={formData.district}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="Enter district name"
                disabled={readOnly}
                error={errors.district}
                touched={touched.district}
                inputClassName={readOnly ? 'bg-gray-50' : ''}
                helperText="Optional - specify district for better location identification"
              />

              {/* Region (Optional) */}
              <FormField
                label="Region"
                name="region"
                type="text"
                value={formData.region}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="Enter region name"
                disabled={readOnly}
                error={errors.region}
                touched={touched.region}
                inputClassName={readOnly ? 'bg-gray-50' : ''}
                helperText="Optional - specify region for better area grouping"
              />
            </div>
          </div>

          {/* Status Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Area Status
            </h3>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Active Status</p>
                <p className="text-sm text-gray-600">
                  {formData.is_active ? 'This area is currently active' : 'This area is currently inactive'}
                </p>
              </div>
              
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => {
                    const newValue = !formData.is_active;
                    setFormData(prev => ({ ...prev, is_active: newValue }));
                    setTouched(prev => ({ ...prev, is_active: true }));
                  }}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    formData.is_active ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.is_active ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              )}
            </div>
          </div>

          {/* Admin Info Display */}
          {isAdmin && existingArea && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Provider Information
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-700">Provider ID:</span>
                  <span className="ml-2 text-sm text-gray-900">{existingArea.provider_id}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Created:</span>
                  <span className="ml-2 text-sm text-gray-900">
                    {new Date(existingArea.created_at).toLocaleDateString()}
                  </span>
                </div>
                {existingArea.updated_at && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Updated:</span>
                    <span className="ml-2 text-sm text-gray-900">
                      {new Date(existingArea.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Submit Button (Mobile) */}
          {!readOnly && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEditMode ? 'Update Area' : 'Create Area'}
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AreaFormPage;